import React, { useEffect, useMemo, useState } from 'react';
import { AlertCircle, Edit2, Eye, EyeOff, Plus, Save, Trash2, X } from 'lucide-react';
import {
  createDepartmentManagementOption,
  deleteDepartmentManagementOption,
  DepartmentManagementOption,
  getDepartmentManagementOptions,
  getPlantProvisionOptions,
  PlantProvisionOption,
  updateDepartmentManagementOption,
} from '../../services/workshopOptions';

interface DepartmentFormData {
  name: string;
  code: string;
  description: string;
  is_active: boolean;
  plant: string;
  plants: number[];
}

const emptyForm: DepartmentFormData = {
  name: '',
  code: '',
  description: '',
  is_active: true,
  plant: '',
  plants: [],
};

const DepartmentOptionsManagement: React.FC = () => {
  const [departments, setDepartments] = useState<DepartmentManagementOption[]>([]);
  const [plants, setPlants] = useState<PlantProvisionOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showInactive, setShowInactive] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState<DepartmentManagementOption | null>(null);
  const [formData, setFormData] = useState<DepartmentFormData>(emptyForm);

  const clearMessages = () => {
    setError(null);
    setSuccess(null);
  };

  const fetchData = async () => {
    setIsLoading(true);
    clearMessages();
    try {
      const [departmentData, plantData] = await Promise.all([
        getDepartmentManagementOptions(),
        getPlantProvisionOptions(),
      ]);
      setDepartments(departmentData);
      setPlants(plantData);
    } catch (err: any) {
      setError(err?.message || 'Failed to load departments.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredDepartments = useMemo(() => {
    let data = [...departments];
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      data = data.filter(
        (dept) =>
          dept.name.toLowerCase().includes(q) ||
          dept.code.toLowerCase().includes(q) ||
          (dept.description || '').toLowerCase().includes(q)
      );
    }
    if (!showInactive) {
      data = data.filter((dept) => dept.is_active);
    }
    return data.sort((a, b) => a.name.localeCompare(b.name));
  }, [departments, searchTerm, showInactive]);

  const handleOpenAdd = () => {
    clearMessages();
    setSelectedDepartment(null);
    setFormData(emptyForm);
    setShowAddModal(true);
  };

  const handleOpenEdit = (department: DepartmentManagementOption) => {
    clearMessages();
    setSelectedDepartment(department);
    setFormData({
      name: department.name,
      code: department.code,
      description: department.description || '',
      is_active: department.is_active,
      plant: department.plant ? String(department.plant) : '',
      plants: department.plants_ids || [],
    });
    setShowEditModal(true);
  };

  const handleOpenDelete = (department: DepartmentManagementOption) => {
    clearMessages();
    setSelectedDepartment(department);
    setShowDeleteModal(true);
  };

  const syncPrimaryPlant = (nextPlants: number[]) => {
    if (formData.plant && !nextPlants.includes(Number(formData.plant))) {
      setFormData((prev) => ({ ...prev, plants: nextPlants, plant: '' }));
      return;
    }
    setFormData((prev) => ({ ...prev, plants: nextPlants }));
  };

  const buildPayload = () => {
    const normalizedName = formData.name.trim();
    const normalizedCode = formData.code.trim();
    const normalizedDescription = formData.description.trim();
    const selectedPlants = new Set(formData.plants);
    const primaryPlant = formData.plant ? Number(formData.plant) : null;

    if (primaryPlant) {
      selectedPlants.add(primaryPlant);
    }

    return {
      name: normalizedName,
      code: normalizedCode,
      description: normalizedDescription,
      is_active: formData.is_active,
      plant: primaryPlant,
      plants: Array.from(selectedPlants),
    };
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError('Department name is required.');
      return false;
    }
    if (!formData.code.trim()) {
      setError('Department code is required.');
      return false;
    }
    if (!formData.plant) {
      setError('Primary plant is required.');
      return false;
    }

    const payload = buildPayload();
    if (payload.plants.length === 0) {
      setError('Select at least one linked plant.');
      return false;
    }
    return true;
  };

  const handleSubmitAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    clearMessages();
    if (!validateForm()) return;
    try {
      setIsSaving(true);
      await createDepartmentManagementOption(buildPayload());
      setSuccess('Department created successfully.');
      setShowAddModal(false);
      await fetchData();
    } catch (err: any) {
      setError(err?.message || 'Failed to create department.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmitEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedDepartment) return;
    clearMessages();
    if (!validateForm()) return;
    try {
      setIsSaving(true);
      await updateDepartmentManagementOption(selectedDepartment.id, buildPayload());
      setSuccess('Department updated successfully.');
      setShowEditModal(false);
      setSelectedDepartment(null);
      await fetchData();
    } catch (err: any) {
      setError(err?.message || 'Failed to update department.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedDepartment) return;
    clearMessages();
    try {
      setIsSaving(true);
      await deleteDepartmentManagementOption(selectedDepartment.id);
      setSuccess('Department deleted successfully.');
      setShowDeleteModal(false);
      setSelectedDepartment(null);
      await fetchData();
    } catch (err: any) {
      setError(err?.message || 'Failed to delete department.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleActive = async (department: DepartmentManagementOption) => {
    clearMessages();
    try {
      await updateDepartmentManagementOption(department.id, {
        is_active: !department.is_active,
      });
      setSuccess(`Department ${department.is_active ? 'deactivated' : 'activated'} successfully.`);
      await fetchData();
    } catch (err: any) {
      setError(err?.message || 'Failed to update department status.');
    }
  };

  const renderForm = (onSubmit: (e: React.FormEvent) => Promise<void>, title: string, submitLabel: string) => (
    <form onSubmit={onSubmit}>
      <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        <button
          type="button"
          onClick={() => {
            setShowAddModal(false);
            setShowEditModal(false);
            setSelectedDepartment(null);
          }}
          className="text-gray-400 hover:text-gray-600"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="px-6 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Department Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Department Code <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.code}
              onChange={(e) => setFormData((prev) => ({ ...prev, code: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            rows={2}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Primary Plant
          </label>
          <select
            value={formData.plant}
            onChange={(e) => {
              const value = e.target.value;
              if (!value) {
                setFormData((prev) => ({ ...prev, plant: '' }));
                return;
              }
              const plantId = Number(value);
              setFormData((prev) => ({
                ...prev,
                plant: value,
                plants: prev.plants.includes(plantId) ? prev.plants : [...prev.plants, plantId],
              }));
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Select primary plant</option>
            {plants.map((plant) => (
              <option key={plant.id} value={plant.id}>
                {plant.name} ({plant.code}) {plant.alv_id ? `- ${plant.alv_id}` : ''}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Linked Plants <span className="text-red-500">*</span>
          </label>
          <div className="max-h-52 overflow-y-auto border border-gray-200 rounded-lg p-3 space-y-2">
            {plants.map((plant) => (
              <label key={plant.id} className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.plants.includes(plant.id)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      syncPrimaryPlant([...formData.plants, plant.id]);
                    } else {
                      syncPrimaryPlant(formData.plants.filter((id) => id !== plant.id));
                    }
                  }}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">
                  {plant.name} ({plant.code}) {plant.alv_id ? `- ${plant.alv_id}` : ''}
                </span>
              </label>
            ))}
            {plants.length === 0 && (
              <div className="text-sm text-gray-500">No plants available.</div>
            )}
          </div>
        </div>

        <div className="flex items-center">
          <input
            type="checkbox"
            checked={formData.is_active}
            onChange={(e) => setFormData((prev) => ({ ...prev, is_active: e.target.checked }))}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <span className="ml-2 text-sm text-gray-700">Active</span>
        </div>
      </div>

      <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
        <button
          type="button"
          onClick={() => {
            setShowAddModal(false);
            setShowEditModal(false);
            setSelectedDepartment(null);
          }}
          className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSaving}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60 flex items-center gap-2"
        >
          <Save className="h-4 w-4" />
          {isSaving ? 'Saving...' : submitLabel}
        </button>
      </div>
    </form>
  );

  return (
    <div className="space-y-6">
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg flex items-center">
          <AlertCircle className="h-5 w-5 mr-2" />
          {success}
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center">
          <AlertCircle className="h-5 w-5 mr-2" />
          {error}
        </div>
      )}

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <input
            type="text"
            placeholder="Search departments..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="px-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowInactive(!showInactive)}
            className={`
              flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors
              ${showInactive
                ? 'bg-green-50 border-green-300 text-green-700'
                : 'bg-gray-100 border-gray-300 text-gray-700'
              }
            `}
          >
            {showInactive ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
            <span className="text-sm font-medium">
              {showInactive ? 'Showing: All Departments' : 'Showing: Active Only'}
            </span>
          </button>

          <button
            onClick={handleOpenAdd}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            <span className="font-medium">Add Department</span>
          </button>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">Loading departments...</div>
        ) : filteredDepartments.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            {searchTerm ? 'No departments match your search.' : 'No departments found.'}
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Primary Plant</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Linked Plants</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredDepartments.map((department) => (
                <tr key={department.id} className={!department.is_active ? 'opacity-50' : ''}>
                  <td className="px-4 py-4 text-sm font-medium text-gray-900">{department.name}</td>
                  <td className="px-4 py-4 text-sm text-gray-600">{department.code}</td>
                  <td className="px-4 py-4 text-sm text-gray-700">{department.plant_name || '-'}</td>
                  <td className="px-4 py-4 text-sm text-gray-600">
                    {department.linked_plants?.length
                      ? department.linked_plants.map((p) => p.code).join(', ')
                      : '-'}
                  </td>
                  <td className="px-4 py-4">
                    <button
                      onClick={() => handleToggleActive(department)}
                      className={`
                        inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                        ${department.is_active
                          ? 'bg-green-100 text-green-800 hover:bg-green-200'
                          : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                        }
                      `}
                    >
                      {department.is_active ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className="px-4 py-4 text-right text-sm font-medium space-x-2">
                    <button
                      onClick={() => handleOpenEdit(department)}
                      className="text-blue-600 hover:text-blue-900 inline-flex items-center"
                      title="Edit"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleOpenDelete(department)}
                      className="text-red-600 hover:text-red-900 inline-flex items-center ml-3"
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="text-sm text-gray-600">
        Showing {filteredDepartments.length} of {departments.length} departments
      </div>

      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
            {renderForm(handleSubmitAdd, 'Add Department', 'Create Department')}
          </div>
        </div>
      )}

      {showEditModal && selectedDepartment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
            {renderForm(handleSubmitEdit, `Edit Department: ${selectedDepartment.name}`, 'Save Changes')}
          </div>
        </div>
      )}

      {showDeleteModal && selectedDepartment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Confirm Deletion</h3>
            </div>
            <div className="px-6 py-4 space-y-2">
              <p className="text-gray-700">
                Delete department <strong>{selectedDepartment.name}</strong>?
              </p>
              <p className="text-sm text-red-600">
                This can fail if workshops still reference this department.
              </p>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedDepartment(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={isSaving}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-60 flex items-center gap-2"
              >
                <Trash2 className="h-4 w-4" />
                {isSaving ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DepartmentOptionsManagement;
